import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AuthService } from '../services/APIService';

// Async thunks for authentication actions
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await AuthService.login(credentials);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await AuthService.register(userData);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Initial state
const initialState = {
  user: AuthService.getCurrentUser(),
  isAuthenticated: AuthService.isAuthenticated(),
  isAdmin: AuthService.isAdmin(),
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null
};

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      AuthService.logout();
      state.user = null;
      state.isAuthenticated = false;
      state.isAdmin = false;
      state.status = 'idle';
      state.error = null;
    },
    // Call this after login to sync Redux from localStorage
    syncFromStorage(state) {
      state.user = AuthService.getCurrentUser();
      state.isAuthenticated = AuthService.isAuthenticated();
      state.isAdmin = AuthService.isAdmin();
      state.status = 'succeeded';
    },
    clearError(state) {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(login.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = AuthService.getCurrentUser();
        state.isAuthenticated = true;
        state.isAdmin = AuthService.isAdmin();
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      // Register cases
      .addCase(register.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(register.fulfilled, (state) => {
        state.status = 'succeeded';
        // Don't auto-login after registration, user should explicitly login
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  }
});

// Export actions and selectors
export const { logout, syncFromStorage, clearError } = authSlice.actions;

export const selectCurrentUser = state => state.auth.user;
export const selectIsAuthenticated = state => state.auth.isAuthenticated;
export const selectIsAdmin = state => state.auth.isAdmin;
export const selectAuthStatus = state => state.auth.status;
export const selectAuthError = state => state.auth.error;

export default authSlice.reducer;
